export type PhaseKey =
  | 'phase_0_bootstrap'
  | 'phase_1_requirements'
  | 'phase_2_architecture'
  | 'phase_2_5_ux_design'
  | 'phase_3_sprint_planning'
  | 'phase_4_development'
  | 'phase_5_code_review'
  | 'phase_6_testing'
  | 'phase_7_deployment'
  | 'phase_8_retrospective';

export const phaseConfig: Record<PhaseKey, { label: string; className: string }> = {
  phase_0_bootstrap:       { label: 'Bootstrap',       className: 'bg-gray-100 text-gray-600' },
  phase_1_requirements:    { label: 'Requirements',    className: 'bg-blue-100 text-blue-700' },
  phase_2_architecture:    { label: 'Architecture',    className: 'bg-purple-100 text-purple-700' },
  phase_2_5_ux_design:     { label: 'UX Design',       className: 'bg-pink-100 text-pink-700' },
  phase_3_sprint_planning: { label: 'Sprint Planning', className: 'bg-amber-100 text-amber-700' },
  phase_4_development:     { label: 'Development',     className: 'bg-green-100 text-green-700' },
  phase_5_code_review:     { label: 'Code Review',     className: 'bg-orange-100 text-orange-700' },
  phase_6_testing:         { label: 'Testing',         className: 'bg-red-100 text-red-700' },
  phase_7_deployment:      { label: 'Deployment',      className: 'bg-teal-100 text-teal-700' },
  phase_8_retrospective:   { label: 'Retrospective',   className: 'bg-gray-100 text-gray-500' },
};

/**
 * Logical SDLC phase number per phase key. Sprint 11 UX.A1 inserted
 * `phase_2_5_ux_design` as a fractional phase between 2 and 3 — using the
 * array index of ALL_PHASES to render a phase number is therefore wrong:
 * `phase_3_sprint_planning` is at array position 4 but its display number
 * is `3`. UI surfaces (dashboard "Current Phase" card, phase progress
 * stepper, gate banners) must read this map instead of `array.indexOf`.
 *
 * Stringified values keep `2.5` rendered verbatim while still allowing
 * direct concatenation (`Phase ${PHASE_DISPLAY_NUMBER[phase]}`).
 */
export const PHASE_DISPLAY_NUMBER: Record<PhaseKey, string> = {
  phase_0_bootstrap:       '0',
  phase_1_requirements:    '1',
  phase_2_architecture:    '2',
  phase_2_5_ux_design:     '2.5',
  phase_3_sprint_planning: '3',
  phase_4_development:     '4',
  phase_5_code_review:     '5',
  phase_6_testing:         '6',
  phase_7_deployment:      '7',
  phase_8_retrospective:   '8',
};

interface PhaseBadgeProps {
  phase: PhaseKey;
  size?: 'sm' | 'lg';
}

export default function PhaseBadge({ phase, size = 'sm' }: PhaseBadgeProps) {
  const config = phaseConfig[phase] ?? { label: phase, className: 'bg-gray-100 text-gray-600' };
  const sizeClass =
    size === 'lg'
      ? 'px-3 py-1.5 text-sm font-semibold'
      : 'px-2 py-0.5 text-xs font-medium';

  return (
    <span className={`inline-flex items-center rounded-full whitespace-nowrap ${sizeClass} ${config.className}`}>
      {config.label}
    </span>
  );
}
